import { useState } from 'react'
import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import { useAppearance } from '@segment/matcha/foundations/AppearanceContext'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import Image from '@segment/matcha/components/Image'

import CustomerCardBase, * as CustomerCard from '@segment/matcha/recipes/CustomerCard'

import useContentfulImage from '@segment/utils/useContentfulImage'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import type { Card } from './Hero'
import { featuredImageFormats, logoImageFormats } from './imageFormats'

interface ListProps {
  data: Card[]
}

export const List: FC<PropsWithChildren<ListProps>> = ({ data }) => {
  return (
    <Wrapper>
      {data.map(({ featuredImage, featuredLogo, slug }, i) => (
        <Item slug={slug} key={i} featuredLogo={featuredLogo} featuredImage={featuredImage} index={i} />
      ))}
    </Wrapper>
  )
}

interface ItemProps {
  index: number
}

const Item: FC<PropsWithChildren<Card & ItemProps>> = ({ slug, featuredImage, featuredLogo, index }) => {
  const pageConfig = usePageConfig()
  const [isHover, setIsHover] = useState(false)
  const logoFile = featuredLogo?.fields?.file
  const [logoOptimizedUrl] = useContentfulImage(logoFile?.url, logoImageFormats) ?? []
  const imageFile = featuredImage?.fields?.file
  const [featuredImageOptimizedUrl] = useContentfulImage(imageFile?.url, featuredImageFormats) ?? []
  const appearance = useAppearance()
  const buttonAppearance = appearance === `twilioLight` ? `dark` : undefined

  return (
    <ItemWrapper
      href={getAbsoluteUrl(`/customers/${slug}/`, pageConfig)}
      img={featuredImageOptimizedUrl}
      key={index}
      hoverable={true}
      area={`item${index}`}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <CustomerCard.Row>
        {logoOptimizedUrl && (
          <Logo
            loader={() => logoOptimizedUrl}
            width={logoImageFormats.width}
            height={logoImageFormats.height}
            src={logoOptimizedUrl}
            alt="Customer Card"
          />
        )}
      </CustomerCard.Row>
      <CustomerCard.Row>
        <ButtonText
          size="small"
          hover={isHover}
          variant="mono"
          iconEnd={ChevronRightIcon}
          appearance={buttonAppearance}
        >
          Read their story
        </ButtonText>
      </CustomerCard.Row>
    </ItemWrapper>
  )
}

const Wrapper = styled.div`
  margin: 0 auto;
  display: none;
  column-gap: 25px;
  width: 100%;
  max-width: 678px;
  ${({ theme }) => theme.breakpoints.up(`md`)} {
    display: grid;
    grid-template:
      'item0 item0 item1' 250px
      '. . item1' 25px
      'item2 item3 item1' 25px
      'item2 item3 .' 25px
      'item2 item3 item4' 250px
      'item2 . item4' 25px / 1fr 1fr 1fr;
  }

  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    grid-template:
      'item0 item0' 250px
      '. .' 25px
      'item2 item3' 300px
      'item2 .' 25px / 1fr 1fr;
  }

  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    grid-template:
      'item0 item0 item1' 250px
      '. . item1' 25px
      'item2 item3 item1' 25px
      'item2 item3 .' 25px
      'item2 item3 item4' 250px
      'item2 . item4' 25px / 1fr 1fr 1fr;
  }
`
interface ItemWrapperProps {
  area: string
}

const Logo = styled(Image)`
  max-width: 130px;
  width: auto;
  height: 30px;
  margin: 0;
`

const ItemWrapper = styled(CustomerCardBase)<ItemWrapperProps>`
  grid-area: ${({ area }) => area};
  height: auto;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    height: auto;
  }

  ${({ theme }) => theme.breakpoints.between(`xl`, `xl`)} {
    :nth-of-type(2) {
      display: none;
    }

    :nth-of-type(5) {
      display: none;
    }
  }
`
